import React, { useState, useEffect, useContext } from 'react';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCustomAlert } from './CustomAlert';
import { 
    faPlus, 
    faUsers, 
    faComments, 
    faBell, 
    faBellSlash,
    faCog,
    faSearch,
    faFilter,
    faEnvelope,
    faCheck,
    faTimes,
    faMapMarkerAlt,
    faCompass
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { timeAgo } from '../utils/functions';
import RichTextEditor from './RichTextEditor';
import MapLocationPicker from './MapLocationPicker';

const JOIN_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

const normalizeJoinCode = (value) => {
    const code = String(value || '').replace(/\s+/g, '').toUpperCase();
    if (code.length !== 6) return null;
    for (let i = 0; i < code.length; i += 1) {
        if (!JOIN_CODE_ALPHABET.includes(code[i])) return null;
    }
    return code;
};

const GroupList = () => {
    const { user } = useContext(MyContext);
    const navigate = useNavigate();
    const { code: joinCodeParam } = useParams();
    const { showAlert, AlertComponent } = useCustomAlert();
    const [groups, setGroups] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [respondingGroupId, setRespondingGroupId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [codeMatch, setCodeMatch] = useState(null);
    const [codeNotFound, setCodeNotFound] = useState(false);
    const [codeLookup, setCodeLookup] = useState(false);
    const [joiningByCode, setJoiningByCode] = useState(false);
    const [showDirectionsModal, setShowDirectionsModal] = useState(false);
    const [filterType, setFilterType] = useState('all');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [prayerCounts, setPrayerCounts] = useState({});
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        console.log('GroupList useEffect triggered, user:', user);
        if (user && user.user_id) {
            console.log('Loading user groups for user_id:', user.user_id);
            let cancelled = false;
            (async () => {
                setLoading(true);
                await Promise.all([
                    loadUserGroups(false),
                    loadInvitations(),
                ]);
                if (!cancelled) setLoading(false);
            })();
            return () => {
                cancelled = true;
            };
        } else {
            console.log('No user or user_id, setting loading to false');
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (groups.length > 0 && user && user.user_id) {
            loadPrayerCounts();
        }
    }, [groups, user]);

    useEffect(() => {
        const code = normalizeJoinCode(joinCodeParam || '');
        if (code) setSearchTerm(code);
    }, [joinCodeParam]);

    useEffect(() => {
        const code = normalizeJoinCode(searchTerm);
        if (!code || !user?.user_id) {
            setCodeMatch(null);
            setCodeNotFound(false);
            setCodeLookup(false);
            setShowDirectionsModal(false);
            return undefined;
        }

        let cancelled = false;
        setCodeLookup(true);
        const timer = setTimeout(async () => {
            try {
                const response = await fetch(`${backendUrl}/find_group_by_code.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user_id: user.user_id, join_code: code })
                });
                const data = await response.json();
                if (!cancelled) {
                    setCodeMatch(data.success ? data.data : null);
                    setCodeNotFound(!data.success);
                }
            } catch (err) {
                if (!cancelled) {
                    setCodeMatch(null);
                    setCodeNotFound(true);
                }
            } finally {
                if (!cancelled) setCodeLookup(false);
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [searchTerm, user]);

    const loadUserGroups = async (manageLoading = true) => {
        try {
            console.log('Starting to load user groups...');
            if (manageLoading) setLoading(true);
            setError(null);
            
            const requestBody = {
                user_id: user.user_id,
                status: 'active'
            };
            
            console.log('Request body:', requestBody);
            //console.log('Backend URL:', backendUrl);
            
            const response = await fetch(`${backendUrl}/get_user_groups.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            console.log('Response status:', response.status);
            const data = await response.json();
            console.log('Response data:', data);

            if (data.success) {
                console.log('Groups loaded successfully:', data.data);
                setGroups(data.data || []);
            } else {
                console.error('Failed to load groups:', data.messages);
                setError(data.messages || 'Failed to load groups');
            }
        } catch (err) {
            console.error('Error loading groups:', err);
            setError('Network error. Please try again later.');
        } finally {
            if (manageLoading) setLoading(false);
        }
    };

    const loadInvitations = async () => {
        if (!user || !user.user_id) return;

        try {
            const response = await fetch(`${backendUrl}/get_group_invitations.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ user_id: user.user_id })
            });
            const data = await response.json();
            if (data.success) {
                setInvitations(data.data || []);
            }
        } catch (err) {
            console.error('Error loading group invitations:', err);
        }
    };

    const respondToInvitation = async (invitation, action) => {
        if (respondingGroupId) return;

        setRespondingGroupId(invitation.group_id);
        try {
            const response = await fetch(`${backendUrl}/accept_group_invitation.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id,
                    group_id: invitation.group_id,
                    action
                })
            });
            const data = await response.json();

            if (data.success) {
                setInvitations((prev) => prev.filter((item) => item.group_id !== invitation.group_id));
                if (action === 'accept') {
                    showAlert('success', 'Joined', `You are now a member of ${invitation.group_name}.`);
                    await loadUserGroups();
                } else {
                    showAlert('info', 'Invitation declined', `You declined the invitation to ${invitation.group_name}.`);
                }
            } else {
                showAlert('error', 'Could not respond', data.messages || 'Could not respond to the invitation');
                loadInvitations();
            }
        } catch (err) {
            console.error('Error responding to group invitation:', err);
            showAlert('error', 'Could not respond', 'Could not respond to the invitation. Please try again.');
        } finally {
            setRespondingGroupId(null);
        }
    };

    const codeMatchHasLocation = (() => {
        const lat = codeMatch?.latitude != null ? Number(codeMatch.latitude) : null;
        const lng = codeMatch?.longitude != null ? Number(codeMatch.longitude) : null;
        return lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng);
    })();
    const showChurchMap = String(codeMatch?.group_type || '').toLowerCase() === 'church' && codeMatchHasLocation;

    const openDirectionsFromMyLocation = () => {
        const lat = codeMatch?.latitude != null ? Number(codeMatch.latitude) : null;
        const lng = codeMatch?.longitude != null ? Number(codeMatch.longitude) : null;
        if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return;
        if (!navigator.geolocation) {
            window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
            setShowDirectionsModal(false);
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const o = `${pos.coords.latitude},${pos.coords.longitude}`;
                const d = `${lat},${lng}`;
                window.open(`https://www.google.com/maps/dir/?api=1&origin=${o}&destination=${d}`, '_blank');
                setShowDirectionsModal(false);
            },
            () => {
                window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
                setShowDirectionsModal(false);
            }
        );
    };

    const joinByCode = async () => {
        if (joiningByCode || !user?.user_id || !codeMatch?.group_id) return;
        if (codeMatch.already_member || codeMatch.has_pending_request) return;

        setJoiningByCode(true);
        try {
            const response = await fetch(`${backendUrl}/create_group_join_request.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: user.user_id,
                    group_id: codeMatch.group_id
                })
            });
            const data = await response.json();
            if (data.success) {
                setCodeMatch((prev) => (prev ? { ...prev, has_pending_request: true } : prev));
                showAlert('success', 'Request sent', 'Your request has been sent. Admins will be notified.');
            } else if (data.error && data.error.toLowerCase().includes('already a member')) {
                setCodeMatch((prev) => (prev ? { ...prev, already_member: true } : prev));
                showAlert('info', 'Already a member', data.error);
            } else if (data.error && data.error.toLowerCase().includes('already')) {
                setCodeMatch((prev) => (prev ? { ...prev, has_pending_request: true } : prev));
                showAlert('info', 'Request pending', data.error);
            } else {
                showAlert('error', 'Could not request', data.error || data.messages || 'Could not request membership');
            }
        } catch (err) {
            showAlert('error', 'Could not request', 'Could not request membership');
        } finally {
            setJoiningByCode(false);
        }
    };

    const handleRefresh = () => {
        console.log('Manual refresh triggered');
        setRefreshKey(prev => prev + 1);
        if (user && user.user_id) {
            loadUserGroups();
            loadInvitations();
        }
    };

    const loadPrayerCounts = async () => {
        try {
            const counts = {};
            
            // Load prayer counts for each group
            const promises = groups.map(async (group) => {
                try {
                    const response = await fetch(`${backendUrl}/get_group_prayer_count.php`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            group_id: group.group_id,
                            user_id: user.user_id
                        })
                    });

                    const data = await response.json();
                    if (data.success) {
                        counts[group.group_id] = data.unread_count;
                    }
                } catch (err) {
                    console.error(`Error loading prayer count for group ${group.group_id}:`, err);
                    counts[group.group_id] = 0;
                }
            });

            await Promise.all(promises);
            setPrayerCounts(counts);
        } catch (err) {
            console.error('Error loading prayer counts:', err);
        }
    };

    const filteredGroups = groups.filter(group => {
        const matchesSearch = group.group_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            group.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterType === 'all' || group.group_type === filterType;
        return matchesSearch && matchesFilter;
    });

    const filteredInvitations = invitations.filter((invitation) => {
        const matchesSearch = !searchTerm ||
            invitation.group_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            invitation.description?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesFilter = filterType === 'all' || invitation.group_type === filterType;
        return matchesSearch && matchesFilter;
    });

    // Format time for display using the existing timeAgo function
    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        return timeAgo(timestamp);
    };

    const getGroupTypeIco_bk = (type) => {
        switch (type) {
            case 'church': return '⛪';
            case 'ministry': return '🙏';
            case 'community': return '👥';
            default: return '💬';
        }
    };

    const getGroupTypeIcon = (type) => {
        return '⛪';
    };

    const getGroupTypeColor = (type) => {
        switch (type) {
            case 'church': return 'text-primary';
            case 'ministry': return 'text-success';
            case 'community': return 'text-info';
            default: return 'text-secondary';
        }
    };

    const handleGroupSettings = (group) => {
        // For now, show an alert with group info
        // Later this can be expanded to show a proper settings modal
        showAlert('info', 'Group Settings', `Group Settings for: ${group.group_name}\n\nRole: ${group.role}\nType: ${group.group_type}\nMembers: ${group.member_count}\n\nSettings functionality coming soon!`);
    };


    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="ms-3">Loading groups...</div>
            </div>
        );
    }

    if (!user || !user.user_id) {
        return (
            <div className="container-fluid p-3">
                <div className="alert alert-warning" role="alert">
                    Please log in to view your groups.
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0">
                    <FontAwesomeIcon icon={faUsers} className="me-2" />
                    My Groups
                </h2>
                <button 
                    className="btn btn-primary"
                    onClick={() => {
                        setError(null); // Clear any previous errors
                        setShowCreateModal(true);
                    }}
                >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Create Group
                </button>
            </div>

            {/* Search and Filter */}
            <div className="row mb-3">
                <div className="col-md-8">
                    <div className="input-group">
                        <span className="input-group-text">
                            <FontAwesomeIcon icon={faSearch} />
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Use invite code or search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="col-md-4">
                    <select
                        className="form-select"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                    >
                        <option value="all">All Groups</option>
                        <option value="church">Church</option>
                        <option value="ministry">Ministry</option>
                        <option value="community">Community</option>
                        <option value="general">General</option>
                    </select>
                </div>
            </div>

            {(codeLookup || codeMatch || codeNotFound) && (
                <div className="card mb-3" style={{ backgroundColor: '#f4faf8', borderColor: '#d5ebe4' }}>
                    <div className="card-body">
                        {codeLookup && !codeMatch ? (
                            <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                        ) : codeNotFound && !codeMatch ? (
                            <span className="text-muted">No group found for that code</span>
                        ) : codeMatch ? (
                            <div className="w-100">
                                <div className="fw-bold">{codeMatch.group_name}</div>
                                {String(codeMatch.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() && (
                                    <p className="mb-0 mt-2" style={{ fontSize: 14, color: '#555', lineHeight: '20px' }}>
                                        {String(codeMatch.description).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()}
                                    </p>
                                )}
                                {String(codeMatch.address || '').trim() && !showChurchMap && (
                                    <div className="d-flex align-items-center gap-2 mt-3" style={{ fontSize: 14, color: '#666' }}>
                                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                                        <span>{String(codeMatch.address).trim()}</span>
                                    </div>
                                )}
                                {showChurchMap && (
                                    <div className="mt-3">
                                        {String(codeMatch.address || '').trim() && (
                                            <div className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 14, color: '#666' }}>
                                                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: '#48887b' }} />
                                                <span>{String(codeMatch.address).trim()}</span>
                                            </div>
                                        )}
                                        <div className="overflow-hidden mb-2" style={{ background: '#e0e0e0', borderRadius: 12, height: 180 }}>
                                            <iframe
                                                title="Church location"
                                                src={`https://www.google.com/maps?q=${codeMatch.latitude},${codeMatch.longitude}&z=15&output=embed`}
                                                width="100%"
                                                height="180"
                                                style={{ border: 0, display: 'block' }}
                                                allowFullScreen
                                                loading="lazy"
                                                referrerPolicy="no-referrer-when-downgrade"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="btn w-100"
                                            style={{
                                                background: 'transparent',
                                                color: '#48887b',
                                                border: '1.5px solid #48887b',
                                                fontWeight: 600,
                                                fontSize: 16
                                            }}
                                            onClick={() => setShowDirectionsModal(true)}
                                        >
                                            <FontAwesomeIcon icon={faCompass} className="me-2" />
                                            Get directions
                                        </button>
                                    </div>
                                )}
                                {codeMatch.has_pending_request && (
                                    <div className="small fw-semibold mt-2" style={{ color: '#8a5a00' }}>
                                        Membership request awaiting approval
                                    </div>
                                )}
                                {!codeMatch.has_pending_request && (
                                <div className="mt-2">
                                    {codeMatch.already_member ? (
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={() => navigate(`/group/${codeMatch.group_id}/detail`)}
                                        >
                                            Open
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            disabled={joiningByCode}
                                            onClick={joinByCode}
                                        >
                                            {joiningByCode ? (
                                                <span className="spinner-border spinner-border-sm" role="status" />
                                            ) : (
                                                'Request membership'
                                            )}
                                        </button>
                                    )}
                                </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="alert alert-danger" role="alert">
                    <strong>Error:</strong> {error}
                    <br />
                    <small>Check the browser console for more details.</small>
                    <br />
                    <button 
                        className="btn btn-outline-danger btn-sm mt-2"
                        onClick={handleRefresh}
                    >
                        <FontAwesomeIcon icon={faSearch} className="me-1" />
                        Retry
                    </button>
                </div>
            )}


            {/* Loading State */}
            {loading && (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading your groups...</p>
                </div>
            )}

            {!loading && filteredInvitations.length > 0 && (
                <div className="mb-4">
                    <h5 className="mb-3">
                        <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                        {filteredInvitations.length === 1
                            ? '1 group invitation'
                            : `${filteredInvitations.length} group invitations`}
                    </h5>
                    <div className="row">
                        {filteredInvitations.map((invitation) => {
                            const busy = respondingGroupId === invitation.group_id;
                            return (
                                <div key={`invite-${invitation.group_id}`} className="col-md-6 col-lg-4 mb-3">
                                    <div className="card h-100" style={{ borderColor: '#e6c98a' }}>
                                        <div className="card-body">
                                            <div className="d-flex align-items-start mb-3">
                                                <div className="me-3">
                                                    {invitation.group_photo ? (
                                                        <img
                                                            src={invitation.group_photo}
                                                            alt={invitation.group_name}
                                                            className="rounded-circle"
                                                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: '50px',
                                                                height: '50px',
                                                                backgroundColor: '#fff6e0',
                                                                fontSize: '20px'
                                                            }}
                                                        >
                                                            {getGroupTypeIcon(invitation.group_type)}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="card-title mb-1">{invitation.group_name}</h6>
                                                    <small className="text-muted">
                                                        {invitation.invited_by_name
                                                            ? `Invited by ${invitation.invited_by_name}`
                                                            : 'You have been invited'}
                                                    </small>
                                                </div>
                                                <span className="badge" style={{ backgroundColor: '#fff3cd', color: '#8a5a00' }}>
                                                    Invite
                                                </span>
                                            </div>
                                            {invitation.description && (
                                                <p className="card-text text-muted small mb-3">
                                                    {invitation.description.replace(/<[^>]*>/g, '').slice(0, 140)}
                                                </p>
                                            )}
                                            <div className="d-flex gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary flex-fill"
                                                    disabled={busy}
                                                    onClick={() => {
                                                        if (window.confirm(`Decline the invitation to join ${invitation.group_name}?`)) {
                                                            respondToInvitation(invitation, 'decline');
                                                        }
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                                                    Decline
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary flex-fill"
                                                    disabled={busy}
                                                    onClick={() => respondToInvitation(invitation, 'accept')}
                                                >
                                                    {busy ? (
                                                        <span className="spinner-border spinner-border-sm" role="status" />
                                                    ) : (
                                                        <>
                                                            <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                            Accept
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Groups List */}
            {!loading && filteredGroups.length === 0 && filteredInvitations.length === 0 && !codeMatch ? (
                <div className="text-center py-5">
                    <FontAwesomeIcon icon={faUsers} size="3x" className="text-muted mb-3" />
                    <h5 className="text-muted">No groups found</h5>
                    <p className="text-muted">
                        {searchTerm || filterType !== 'all' 
                            ? 'Try adjusting your search or filter criteria'
                            : 'Create your first group or wait for an invitation'
                        }
                    </p>
                    {!searchTerm && filterType === 'all' && (
                        <button 
                            className="btn btn-primary"
                            onClick={() => {
                        setError(null); // Clear any previous errors
                        setShowCreateModal(true);
                    }}
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-1" />
                            Create Group
                        </button>
                    )}
                </div>
            ) : !loading && (
                <div className="row">
                    {filteredGroups.map((group) => (
                        <div key={group.group_id} className="col-md-6 col-lg-4 mb-3">
                            <div 
                                className="card h-100 group-card"
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/group/${group.group_id}/detail`)}
                            >
                                <div className="card-body w-100">
                                    <div className="d-flex align-items-start mb-3">
                                        <div className="me-3">
                                            {group.group_photo ? (
                                                <img 
                                                    src={group.group_photo} 
                                                    alt={group.group_name}
                                                    className="rounded-circle"
                                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <div 
                                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{ 
                                                        width: '50px', 
                                                        height: '50px', 
                                                        backgroundColor: '#e9ecef',
                                                        fontSize: '20px'
                                                    }}
                                                >
                                                    {getGroupTypeIcon(group.group_type)}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-grow-1">
                                            <h6 className="card-title mb-1 d-flex align-items-center">
                                                {group.group_name}
                                               
                                            </h6>
                                            <small className="text-muted">
                                                {group.member_count} member{group.member_count !== 1 ? 's' : ''}
                                            </small>
                                        </div>
                                        <div className="text-end">
                                            <div className="d-flex align-items-center gap-2">
                                                {prayerCounts[group.group_id] > 0 && (
                                                    <span className="badge bg-warning rounded-pill">
                                                        {prayerCounts[group.group_id]} prayers
                                                    </span>
                                                )}
                                                {group.unread_count > 0 && (
                                                    <span className="badge bg-danger rounded-pill">
                                                        {group.unread_count}
                                                    </span>
                                                )}

                                                { 3 > 4 && (
                                                <div className="dropdown">
                                                    <button 
                                                        className="btn btn-sm btn-outline-secondary"
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleGroupSettings(group);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faCog} />
                                                    </button>
                                                </div>)}
                                            </div>
                                        </div>
                                    </div>

                                    {group.description && (
                                        <div 
                                            className="card-text text-muted small mb-3"
                                            dangerouslySetInnerHTML={{ 
                                                __html: group.description.length > 100 
                                                    ? group.description.substring(0, 100) + '...'
                                                    : group.description
                                            }}
                                        />
                                    )}

                                    {group.recent_message &&  3 > 4 && (
                                        <div className="border-top pt-3">
                                            <div className="d-flex align-items-center">
                                                <FontAwesomeIcon icon={faComments} className="me-2 text-muted" />
                                                <div className="flex-grow-1">
                                                    <small className="text-muted">
                                                        <strong>{group.recent_message.first_name} {group.recent_message.last_name}:</strong>
                                                    </small>
                                                    <p className="mb-0 small text-truncate">
                                                        {group.recent_message.message}
                                                    </p>
                                                </div>
                                                <small className="text-muted">
                                                    {formatTime(group.recent_message.created_at)}
                                                </small>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-3 d-flex justify-content-between align-items-center">
                                        <small className="text-muted">
                                            Role: <span className="badge bg-secondary">{group.role}</span>
                                        </small>
                                        <small className="text-muted">
                                            {formatTime(group.updated_at)}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Group Modal */}
            {showCreateModal && (
                <CreateGroupModal 
                    onClose={() => setShowCreateModal(false)}
                    onGroupCreated={loadUserGroups}
                />
            )}
            {showDirectionsModal && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ background: 'rgba(0,0,0,0.5)', zIndex: 9999, padding: 20 }}
                    onClick={() => setShowDirectionsModal(false)}
                    role="presentation"
                >
                    <div
                        className="bg-white rounded p-4"
                        style={{ maxWidth: 400, width: '100%' }}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                    >
                        <h3 className="h5 mb-1">Get directions</h3>
                        <p className="text-muted mb-3">Open in Google Maps with your current location as start.</p>
                        <button
                            type="button"
                            className="btn w-100 mb-2"
                            style={{ background: '#48887b', color: '#fff', fontWeight: 600 }}
                            onClick={openDirectionsFromMyLocation}
                        >
                            Open in Google Maps
                        </button>
                        <button
                            type="button"
                            className="btn btn-link w-100 text-decoration-none text-muted"
                            onClick={() => setShowDirectionsModal(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
            <AlertComponent />
        </div>
    );
};

// Create Group Modal Component
const CreateGroupModal = ({ onClose, onGroupCreated }) => {
    const { user } = useContext(MyContext);
    const { showAlert, AlertComponent } = useCustomAlert();
    const [formData, setFormData] = useState({
        group_name: '',
        description: '',
        group_type: 'general',
        address: '',
        latitude: null,
        longitude: null
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Clear any previous errors when modal opens and prevent body scroll
    useEffect(() => {
        setError(null);
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.group_name.trim()) {
            setError('Group name is required');
            return;
        }
        
        // Strip HTML tags for validation
        const plainTextDescription = formData.description.replace(/<[^>]*>/g, '');
        
        if (!plainTextDescription.trim()) {
            setError('Description is required');
            return;
        }
        
        if (plainTextDescription.length < 10) {
            setError('Description must be at least 10 characters long');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            let imageUrl = null;
            
            // Upload image file to Cloudinary if provided
            if (imageFile) {
                const uploadFormData = new FormData();
                uploadFormData.append('image', imageFile);
                uploadFormData.append('user_id', user.user_id);
                uploadFormData.append('upload_type', 'general');
                
                const uploadResponse = await fetch(`${backendUrl}/upload_to_cloudinary.php`, {
                    method: 'POST',
                    body: uploadFormData
                });
                
                const uploadData = await uploadResponse.json();
                
                if (uploadData.success && uploadData.data && uploadData.data.secure_url) {
                    imageUrl = uploadData.data.secure_url;
                    console.log('Group logo uploaded successfully:', imageUrl);
                } else {
                    console.error('Group logo upload failed:', uploadData);
                    setError(uploadData.messages || 'Failed to upload group logo');
                    return;
                }
            }

            const requestData = {
                user_id: user.user_id,
                group_name: formData.group_name,
                description: formData.description,
                group_type: formData.group_type,
                group_photo: imageUrl
            };
            if (formData.address != null && String(formData.address).trim() !== '') requestData.address = formData.address.trim();
            if (formData.latitude != null && !Number.isNaN(Number(formData.latitude))) requestData.latitude = Number(formData.latitude);
            if (formData.longitude != null && !Number.isNaN(Number(formData.longitude))) requestData.longitude = Number(formData.longitude);

            console.log('User object:', user);
            console.log('User ID:', user?.user_id);
            console.log('Sending create group request:', requestData);
            console.log('API URL:', `${backendUrl}/create_group.php`);
            console.log('Full request URL:', `${backendUrl}/create_group.php`);
            
            const response = await fetch(`${backendUrl}/create_group.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData)
            });

            const data = await response.json();
            console.log('Create group response:', data);

            if (data.success) {
                onGroupCreated();
                onClose();
            } else {
                console.error('Create group failed:', data);
                setError(data.messages || 'Failed to create group');
            }
        } catch (err) {
            setError('Network error. Please try again later.');
            console.error('Error creating group:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055, position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', overflow: 'auto' }}>
            <div className="modal-dialog modal-dialog-scrollable" style={{ maxHeight: '90vh', margin: '1.75rem auto' }}>
                <div className="modal-content" style={{ maxHeight: '90vh' }}>
                    <div className="modal-header">
                        <h5 className="modal-title">Create New Group</h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                        ></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body" style={{ overflowY: 'auto', maxHeight: 'calc(90vh - 120px)' }}>
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="group_name" className="form-label">Group Name *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="group_name"
                                    value={formData.group_name}
                                    onChange={(e) => setFormData({...formData, group_name: e.target.value})}
                                    placeholder="Enter group name"
                                    maxLength={255}
                                    required
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="description" className="form-label">Description *</label>
                                <RichTextEditor
                                    id="create-group-description"
                                    value={formData.description}
                                    onChange={(content) => setFormData({...formData, description: content})}
                                    placeholder="Describe your group (required)"
                                    height={200}
                                    maxLength={1000}
                                />
                            </div>

                            <div className="mb-3">
                                <label htmlFor="group_type" className="form-label">Group Type</label>
                                <select
                                    className="form-select"
                                    id="group_type"
                                    value={formData.group_type}
                                    onChange={(e) => setFormData({...formData, group_type: e.target.value})}
                                >
                                    <option value="general">General</option>
                                    <option value="church">Church</option>
                                    <option value="ministry">Ministry</option>
                                    <option value="community">Community</option>
                                </select>
                            </div>

                            {formData.group_type === 'church' && (
                                <div className="mb-3">
                                    <label className="form-label">Address &amp; map location</label>
                                    <p className="small text-muted">Set address and map pointer so this church appears in Find a church near you.</p>
                                    <MapLocationPicker
                                        initialAddress={formData.address || ''}
                                        initialLatitude={formData.latitude}
                                        initialLongitude={formData.longitude}
                                        onLocationSelect={({ address, latitude, longitude }) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                address: address != null ? address : prev.address,
                                                latitude: latitude != null ? latitude : prev.latitude,
                                                longitude: longitude != null ? longitude : prev.longitude
                                            }));
                                        }}
                                    />
                                </div>
                            )}

                            <div className="mb-3">
                                <label htmlFor="group_logo" className="form-label">Group Logo (Optional)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    id="group_logo"
                                    accept="image/*"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setImageFile(file);
                                            const reader = new FileReader();
                                            reader.onload = (e) => setImagePreview(e.target.result);
                                            reader.readAsDataURL(file);
                                        } else {
                                            setImageFile(null);
                                            setImagePreview(null);
                                        }
                                    }}
                                />
                                {imagePreview && (
                                    <div className="mt-2">
                                        <img 
                                            src={imagePreview} 
                                            alt="Group logo preview" 
                                            className="img-thumbnail" 
                                            style={{ maxWidth: '150px', maxHeight: '150px' }}
                                        />
                                    </div>
                                )}
                                <div className="form-text">
                                    Upload a logo for your group. You can add or change this later.
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                type="button" 
                                className="btn btn-secondary" 
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                        Creating...
                                    </>
                                ) : (
                                    'Create Group'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            
            {/* Custom Alert Component */}
            <AlertComponent />
        </div>
    );
};

export default GroupList;